import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  Settings,
  Shield,
  Sparkles,
  Search,
  Crown,
  Loader2,
  Check,
  X,
  AlertTriangle,
  TrendingUp,
  Zap,
  UserCog,
  Package,
  Disc,
  DollarSign,
  UserCheck,
  UserPlus,
  Archive,
  ShoppingBag,
  Download,
} from 'lucide-react';
import { api } from '../services/api';
import { Pagination } from '../components/common/Pagination';
import type { UserForAdminWithStats } from '@findmystuff/shared';

export function AdminPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingGlobalLimit, setEditingGlobalLimit] = useState(false);
  const [newGlobalLimit, setNewGlobalLimit] = useState('');

  // Get filter state from URL
  const page = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || '';

  // Helper to update URL params
  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '' || (key === 'page' && value === '1')) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });
      return params;
    });
  };

  const setPage = (newPage: number) => {
    updateParams({ page: String(newPage) });
  };

  const setSearch = (value: string) => {
    updateParams({ search: value, page: '1' });
  };

  // Check if user is admin
  const { data: adminCheck, isLoading: checkingAdmin } = useQuery({
    queryKey: ['adminCheck'],
    queryFn: () => api.admin.checkAdmin(),
  });

  // Get admin stats
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: () => api.admin.getStats(),
    enabled: adminCheck?.isAdmin === true,
  });

  // Get users list
  const { data: usersData, isLoading: loadingUsers } = useQuery({
    queryKey: ['adminUsers', { search, page }],
    queryFn: () => api.admin.getUsers({ search: search || undefined, page }),
    enabled: adminCheck?.isAdmin === true,
  });

  // Mutations
  const updateAdminMutation = useMutation({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) =>
      api.admin.updateUserAdmin(userId, isAdmin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (limit: number) => api.admin.updateSettings(limit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
      setEditingGlobalLimit(false);
    },
  });

  const [isDownloadingBackup, setIsDownloadingBackup] = useState(false);

  const handleDownloadBackup = async () => {
    if (!confirm('Download a full database backup? This may take a moment for large databases.')) {
      return;
    }
    setIsDownloadingBackup(true);
    try {
      await api.admin.downloadBackup();
    } catch (error) {
      console.error('Backup download failed:', error);
      alert('Failed to download backup. Please try again.');
    } finally {
      setIsDownloadingBackup(false);
    }
  };

  // Loading state
  if (checkingAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Not admin - redirect
  if (!adminCheck?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  const users = usersData?.users || [];
  const totalPages = usersData?.meta?.totalPages || 1;
  const totalCount = usersData?.meta?.totalCount || 0;

  const handleSaveGlobalLimit = () => {
    const limit = parseInt(newGlobalLimit, 10);
    if (isNaN(limit) || limit < 1) return;
    updateSettingsMutation.mutate(limit);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600">Manage users and AI credit settings</p>
          </div>
        </div>
        <button
          onClick={handleDownloadBackup}
          disabled={isDownloadingBackup}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isDownloadingBackup ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {isDownloadingBackup ? 'Downloading...' : 'Backup Database'}
        </button>
      </div>

      {/* User Stats Cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Users</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.totalUsers}
                </p>
                <p className="text-sm text-gray-500">Total Users</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.activeUsersLast30Days}
                </p>
                <p className="text-sm text-gray-500">Active (30d)</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <UserPlus className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.newUsersLast7Days}
                </p>
                <p className="text-sm text-gray-500">New (7d)</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Disc className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.usersWithDiscogs}
                </p>
                <p className="text-sm text-gray-500">Discogs Connected</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Stats Cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Content</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Package className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.totalItems?.toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">Total Items</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Archive className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.totalInventories}
                </p>
                <p className="text-sm text-gray-500">Inventories</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <ShoppingBag className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.totalDiscogsListings}
                </p>
                <p className="text-sm text-gray-500">Discogs Listings</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : `$${(stats?.totalListingValue || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                </p>
                <p className="text-sm text-gray-500">Total Value</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Stats Cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">AI Usage</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.totalAiCreditsUsed}
                </p>
                <p className="text-sm text-gray-500">Total AI Uses</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.usersAtLimit}
                </p>
                <p className="text-sm text-gray-500">At Limit</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Crown className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.adminUsers}
                </p>
                <p className="text-sm text-gray-500">Admins</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loadingStats ? '-' : stats?.totalSoldItems}
                </p>
                <p className="text-sm text-gray-500">Items Sold</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Global Settings */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Global Settings</h2>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <div>
              <p className="font-medium text-gray-900">Default AI Credits Limit</p>
              <p className="text-sm text-gray-500">
                New users get this many free AI credits
              </p>
            </div>
          </div>

          {editingGlobalLimit ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                value={newGlobalLimit}
                onChange={(e) => setNewGlobalLimit(e.target.value)}
                className="w-24 px-3 py-2 text-center border rounded-lg focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
              <button
                onClick={handleSaveGlobalLimit}
                disabled={updateSettingsMutation.isPending}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
              >
                {updateSettingsMutation.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => setEditingGlobalLimit(false)}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setNewGlobalLimit(String(stats?.defaultAiCreditsLimit || 5));
                setEditingGlobalLimit(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors font-semibold"
            >
              <TrendingUp className="w-4 h-4" />
              {loadingStats ? '-' : stats?.defaultAiCreditsLimit} credits
            </button>
          )}
        </div>
      </div>

      {/* Users List */}
      <div className="card">
        <div className="p-4 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <UserCog className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 w-full sm:w-64"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Sold</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">AI Used</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Admin</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loadingUsers ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user: UserForAdminWithStats) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    defaultLimit={stats?.defaultAiCreditsLimit || 5}
                    onToggleAdmin={(userId, isAdmin) =>
                      updateAdminMutation.mutate({ userId, isAdmin })
                    }
                    isUpdatingAdmin={updateAdminMutation.isPending}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={20}
            onPageChange={setPage}
          />
        </div>
      </div>
    </div>
  );
}

// User Row Component
function UserRow({
  user,
  defaultLimit,
  onToggleAdmin,
  isUpdatingAdmin,
}: {
  user: UserForAdminWithStats;
  defaultLimit: number;
  onToggleAdmin: (userId: string, isAdmin: boolean) => void;
  isUpdatingAdmin: boolean;
}) {
  const effectiveLimit = user.aiCreditsLimit ?? defaultLimit;
  const isAtLimit = !user.isAdmin && user.aiCreditsUsed >= effectiveLimit;

  // Format date to relative time
  const formatDate = (date: Date) => {
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <tr className={isAtLimit ? 'bg-red-50' : 'hover:bg-gray-50'}>
      {/* User info */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {user.displayName.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-gray-900 flex items-center gap-2">
              {user.displayName}
              {user.isAdmin && (
                <Crown className="w-4 h-4 text-amber-500" />
              )}
            </p>
            <p className="text-xs text-gray-500">{user.email}</p>
            <p className="text-xs text-gray-400">
              Joined {formatDate(user.createdAt)}
              {user.lastActiveAt && ` • Active ${formatDate(user.lastActiveAt)}`}
            </p>
          </div>
        </div>
      </td>

      {/* Items count */}
      <td className="px-4 py-3 text-center">
        <div>
          <span className="font-semibold text-gray-900">{user.itemCount}</span>
          <p className="text-xs text-gray-500">{user.inventoryCount} inv</p>
        </div>
      </td>

      {/* Sold items */}
      <td className="px-4 py-3 text-center">
        <span className={`font-semibold ${user.soldItemCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
          {user.soldItemCount}
        </span>
      </td>

      {/* Total listing value */}
      <td className="px-4 py-3 text-center">
        <span className={`font-semibold ${user.totalListingValue > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
          {user.totalListingValue > 0
            ? `$${user.totalListingValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
            : '-'
          }
        </span>
      </td>

      {/* AI Credits used */}
      <td className="px-4 py-3 text-center">
        {user.isAdmin ? (
          <span className="text-sm text-gray-400">∞</span>
        ) : (
          <span className={`font-semibold ${isAtLimit ? 'text-red-600' : 'text-gray-900'}`}>
            {user.aiCreditsUsed}/{effectiveLimit}
          </span>
        )}
      </td>

      {/* Admin toggle */}
      <td className="px-4 py-3 text-center">
        <button
          onClick={() => onToggleAdmin(user.id, !user.isAdmin)}
          disabled={isUpdatingAdmin}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            user.isAdmin
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {user.isAdmin ? (
            <>
              <Shield className="w-3.5 h-3.5" />
              Admin
            </>
          ) : (
            'User'
          )}
        </button>
      </td>

      {/* Actions - empty for now, can be expanded */}
      <td className="px-4 py-3 text-right">
        {/* Future: Add dropdown menu with more actions */}
      </td>
    </tr>
  );
}
